// Signing in through the Israeli system, both directions.
//
// Israeli staff hold no password here, so on the Israel-tenant host there is no
// login form at all: anybody without a session is sent to clinicUI with the
// page they wanted, and comes back with a signed assertion in the URL fragment.
//
// One file for both halves because they have to agree about two things - the
// fragment's shape, and what counts as a safe place to come back to.
import { ilClinicUiUrl, isIlTenantApp } from './appMode';

// How many times we will bounce to clinicUI before giving up and saying so.
// Without a cap, a user whose account is not synced would be thrown between
// the two systems for ever, each side correctly deciding the other should
// handle them.
const MAX_ATTEMPTS = 2;
const ATTEMPTS_KEY = 'ilHandoffAttempts';

function attempts(): number {
  try {
    return parseInt(sessionStorage.getItem(ATTEMPTS_KEY) || '0', 10) || 0;
  } catch {
    return 0;   // private window, blocked storage: treat as a first attempt
  }
}

function countAttempt(): void {
  try {
    sessionStorage.setItem(ATTEMPTS_KEY, String(attempts() + 1));
  } catch {
    // Not being able to count is not a reason to refuse the redirect.
  }
}

export function clearHandoffAttempts(): void {
  try {
    sessionStorage.removeItem(ATTEMPTS_KEY);
  } catch {
    // Nothing to clear.
  }
}

/** Only a path on this host - never an address somewhere else.
 *
 * The return location arrives from a URL, so it is attacker-controlled: if it
 * could name a host, a link that legitimately signs somebody in could also
 * land them on a copy of this app. A scheme, a protocol-relative //host, or a
 * value that is not an absolute path all become the calendar.
 */
export function safeReturnPath(value: string | null | undefined): string {
  const path = (value || '').trim();
  if (!path.startsWith('/') || path.startsWith('//') || path.includes(':')) {
    return '/calendar';
  }
  return path;
}

// A navigation away from this page is already under way.
//
// window.location.replace() does not stop the page: React carries on, and
// whatever renders next can replace the url again - last call wins. Logging
// out hits exactly that. logout() clears the user BEFORE it redirects, the app
// re-renders into IlLoginRedirectPage, and its effect fires redirectToIlLogin()
// - the ordinary sign-in url, with no uk_logout on it. That lands on clinicUI
// with its session untouched, which mints a fresh assertion and returns the
// person signed in: Log out, blink, back in, which is what was reported.
//
// So the first redirect latches, and later ones are no-ops. The browser is
// leaving; there is nothing for them to do but undo the decision already made.
let leaving = false;

/** Send this visitor to the Israeli system to sign in, and come back here.
 *
 * Returns false when the cap has been reached, so the caller shows the reason
 * instead of bouncing again.
 */
export function redirectToIlLogin(returnTo?: string): boolean {
  // Already on the way out (a logout): do not steer it somewhere else.
  if (leaving) return true;
  if (attempts() >= MAX_ATTEMPTS) return false;
  countAttempt();
  const target = returnTo || window.location.pathname + window.location.search;
  window.location.replace(
    `${ilClinicUiUrl()}/?uk_return=${encodeURIComponent(safeReturnPath(target))}`,
  );
  return true;
}

/** The assertion and return path from the fragment, removed from the address bar.
 *
 * The fragment is where Israel puts them precisely because it never reaches a
 * server - no CloudFront log, no ALB log, no Referer header. It is still a
 * credential in browser history, so it is cleared as soon as it is read.
 */
export function readHandoffFromFragment(): { token: string; returnTo: string } {
  const raw = (window.location.hash || '').replace(/^#/, '');
  const params = new URLSearchParams(raw);
  const token = params.get('t') || '';
  const returnTo = safeReturnPath(params.get('r'));
  if (token) {
    try {
      window.history.replaceState(
        {}, document.title, window.location.pathname + window.location.search,
      );
    } catch {
      // Leaving it in the bar is ugly, not dangerous: it expires in a minute.
    }
  }
  return { token, returnTo };
}

/** Sign out of BOTH systems, and come back here after signing in again.
 *
 *  A logout here cannot simply end the UK session: clinicUI still holds a live
 *  one, so the next sign-in link would be minted without anybody typing
 *  anything - log out, blink, and you are back in. Israel is where the
 *  identity lives, so a logout has to reach Israel.
 *
 *  It cannot reach it directly either: clinicUI is a different origin, and its
 *  session is in ITS localStorage, which this page cannot touch. So it is
 *  asked, with `uk_logout=1`, and clinicUI clears its own token before it
 *  renders (see utils/ukReturn.ts there) and shows its login screen.
 *
 *  `uk_return` rides along so that signing in brings the person back to the UK
 *  page they were on, rather than leaving them in clinicUI wondering where the
 *  other system went.
 */
export function redirectToIlLogout(returnTo?: string): void {
  leaving = true;
  clearHandoffAttempts();   // a deliberate act: start the counter fresh
  const target = returnTo || window.location.pathname + window.location.search;
  window.location.replace(
    `${ilClinicUiUrl()}/?uk_logout=1&uk_return=${encodeURIComponent(safeReturnPath(target))}`,
  );
}

/** Where an expired session goes on this host. True when it was handled.
 *
 *  Always the Israeli system: this host has no login form of its own, so
 *  there is no other place for an expired session to land. Somebody who signs
 *  in with a UK password does so on the ordinary clinic host, where the
 *  ordinary expiry handling applies.
 */
export function handleIlSessionExpiry(): boolean {
  if (!isIlTenantApp()) return false;
  if (leaving) return true;
  return redirectToIlLogin();
}
